/**
 * Landings — the page each visit began on, and which pages turn the new people
 * they bring into readers.
 *
 * Two populations side by side, never mixed: VISITS (every visit that began on
 * the page) and NEW PEOPLE (whose first account was made in the window, each
 * counted once — on the page their first visit landed on). The step columns
 * are of the new people, with the rules the Acquisition tab uses. Definitions
 * live in the backend (`admin/analytics/landings.ts`).
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DataTable, InfoTip, type ColumnDef, type ColumnGroup, type SortState } from '@shared-ui/components';
import { siteLandingsApi, type LandingRow, type SiteScope } from '../../../api/siteAnalyticsApi';

type StepKey = 'openedSimulation' | 'openedTwoSimulations' | 'usedLab' | 'signedUp' | 'cameBack';

const STEPS: { key: StepKey; label: string; title: string }[] = [
  { key: 'openedSimulation', label: 'Simulation', title: 'New people who opened any simulation' },
  { key: 'openedTwoSimulations', label: '2+ sims', title: 'New people who opened two different simulations' },
  { key: 'usedLab', label: 'Lab', title: 'New people who used the chemistry lab (mixed at least once)' },
  { key: 'signedUp', label: 'Signed up', title: 'New people with an account that is no longer a guest' },
  { key: 'cameBack', label: 'Came back', title: 'New people active on two or more different days (UTC)' },
];

const share = (n: number, of: number) => (of > 0 ? Math.round((n / of) * 100) : 0);
/** A share as shown: a real but small one says "<1%", never a "0%" that reads as none. */
const shareLabel = (n: number, of: number) => (n > 0 && share(n, of) === 0 ? '<1%' : `${share(n, of)}%`);

/** A count with its share of `of` hung beside it — the share is what compares
 *  across pages of very different sizes. */
function Count({ n, of }: { n: number; of: number }) {
  return (
    <span className="acq-n">
      {n.toLocaleString()}
      {of > 0 && n !== of && n > 0 && <span className="acq-pct">{shareLabel(n, of)}</span>}
    </span>
  );
}

export default function SiteLandings({ from, to, scope = {} }: { from: Date; to: Date; scope?: SiteScope }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['site-landings', from.toISOString(), to.toISOString(), scope],
    queryFn: () => siteLandingsApi.get(from, to, scope),
  });
  const [sort, setSort] = useState<SortState>({ sortBy: 'visits', sortOrder: 'desc' });
  const [query, setQuery] = useState('');
  const [onlyNew, setOnlyNew] = useState(false);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.rows ?? [])
      .filter(r => !q || r.page.toLowerCase().includes(q))
      .filter(r => !onlyNew || r.newPeople > 0);
  }, [data, query, onlyNew]);

  const columns = useMemo<ColumnDef<LandingRow>[]>(() => [
    { key: 'page', header: 'Landing page', sortable: true, sortValue: r => r.page,
      render: r => <span className="lp-page">{r.page}</span> },
    { key: 'visits', header: <span title="Sessions that began on this page">Sessions</span>, sortable: true,
      className: 'sa-num', sortValue: r => r.visits, render: r => r.visits.toLocaleString() },
    { key: 'leftAfterOnePage', header: <span title="Of those sessions, ones that were a single page view and nothing else">1‑page</span>,
      sortable: true, className: 'sa-num', sortValue: r => (r.visits ? r.leftAfterOnePage / r.visits : 0),
      render: r => <Count n={r.leftAfterOnePage} of={r.visits} /> },
    { key: 'people', header: <span title="Distinct people with a session that began here">People</span>, sortable: true,
      className: 'sa-num', sortValue: r => r.people, render: r => r.people.toLocaleString() },
    { key: 'newPeople', header: <span title="People whose first account was made in this window and whose FIRST session began here. Each new person is in one row only.">New people</span>,
      sortable: true, className: 'sa-num', sortValue: r => r.newPeople, render: r => r.newPeople.toLocaleString() },
    ...STEPS.map((s): ColumnDef<LandingRow> => ({
      key: s.key, header: <span title={s.title}>{s.label}</span>, sortable: true, className: 'sa-num',
      // Sorted by the SHARE of new people, which is the comparison worth making —
      // by count, the busiest page always wins.
      sortValue: r => (r.newPeople ? r[s.key] / r.newPeople : -1),
      render: r => <Count n={r[s.key]} of={r.newPeople} />,
    })),
  ], []);
  // Which population each column counts — said in the header, not left to the note.
  const groups: ColumnGroup[] = [
    { key: 'page', label: '', span: 1 },
    { key: 'all', label: 'All sessions that landed here', span: 3 },
    { key: 'new', label: 'New people, by their first landing', span: 1 + STEPS.length, className: 'lp-group-new' },
  ];

  if (isLoading) return <p className="sa-empty">Loading…</p>;
  if (error || !data) return <p className="sa-error">{(error as Error)?.message ?? 'Could not load landings.'}</p>;

  const t = data.totals;
  return (
    <>
    <section className="sa-panel">
      <h2>
        Landings — where sessions began
        <InfoTip>
          <span className="sa-tip-p">
            One row per page a session <strong>began</strong> on. <strong>Sessions</strong> counts every session that landed
            there. <strong>New people</strong> are people whose first account was made in this window, each counted once —
            on the page their <em>first</em> session landed on — and the step columns are how many of those new people went
            on to do each thing, with the share beside it. Sort a step column to rank pages by that share.
          </span>
          {data.excludedPeople > 0 && (
            <span className="sa-tip-p">
              {data.excludedPeople} internal {data.excludedPeople === 1 ? 'person' : 'people'} (testers, seeded accounts) left out.
            </span>
          )}
        </InfoTip>
      </h2>
      <div className="sa-stats">
        <div className="sa-stat"><span className="sa-stat-value">{t.visits.toLocaleString()}</span><span className="sa-stat-label">Sessions</span><span className="sa-stat-note">across {data.rows.length} landing pages</span></div>
        <div className="sa-stat"><span className="sa-stat-value">{shareLabel(t.leftAfterOnePage, t.visits)}</span><span className="sa-stat-label">Left after one page</span><span className="sa-stat-note">{t.leftAfterOnePage.toLocaleString()} sessions</span></div>
        <div className="sa-stat"><span className="sa-stat-value">{t.people.toLocaleString()}</span><span className="sa-stat-label">People</span><span className="sa-stat-note">with a session here</span></div>
        <div className="sa-stat"><span className="sa-stat-value">{t.newPeople.toLocaleString()}</span><span className="sa-stat-label">New people</span><span className="sa-stat-note">first account made in this window</span></div>
      </div>
      <DataTable<LandingRow>
        columns={columns}
        columnGroups={groups}
        data={rows}
        rowKey={r => r.page}
        sort={sort}
        onSortChange={setSort}
        onSearch={setQuery}
        searchPlaceholder="Filter landing pages…"
        toolbar={(
          <label className="lp-toggle">
            <input type="checkbox" checked={onlyNew} onChange={e => setOnlyNew(e.target.checked)} />
            Only pages that brought new people
          </label>
        )}
        emptyMessage="No landing page matches."
        compact
      />
    </section>
    </>
  );
}
